import type { ChatMessagePage, ChatMessageView } from './views';

/**
 * A thread's history as React Query's infinite query caches it: newest page
 * first, each page newest-first. Restated here rather than imported so this
 * module stays free of any data-fetching library.
 */
export interface MessagePages {
  pages: ChatMessagePage[];
  pageParams: unknown[];
}

/** Every loaded message, newest first — the order each page already uses. */
export function flattenMessagePages(data: MessagePages | undefined): ChatMessageView[] {
  return data?.pages.flatMap((page) => page.messages) ?? [];
}

/**
 * Inserts or replaces one message in a thread's cached pages.
 *
 * Replaces by id rather than appending, because the same message arrives twice
 * on the sender's device — once as the POST response and once over the socket
 * — and the two paths must converge on one entry. A replacement keeps the
 * cached copy's `mine` when it was already true: the broadcast forces `mine`
 * false, because one payload goes to every member.
 *
 * An unseen message belongs at the head of the first (newest) page. With no
 * cache at all a single-message page is seeded — callers that must not seed
 * (a socket push for a thread never opened) check for a cache first.
 */
export function upsertMessage(
  data: MessagePages | undefined,
  message: ChatMessageView,
): MessagePages {
  if (!data || data.pages.length === 0) {
    return { pages: [{ messages: [message], nextCursor: null }], pageParams: [undefined] };
  }
  const known = data.pages.some((page) => page.messages.some((m) => m.id === message.id));
  return {
    ...data,
    pages: data.pages.map((page, index) => {
      if (known) {
        return {
          ...page,
          messages: page.messages.map((m) =>
            m.id === message.id ? { ...message, mine: m.mine || message.mine } : m,
          ),
        };
      }
      return index === 0 ? { ...page, messages: [message, ...page.messages] } : page;
    }),
  };
}

/**
 * Marks one message withdrawn, in place.
 *
 * Tombstoned rather than removed: dropping it would reflow the thread around a
 * gap and lose the record that something was said at all.
 */
export function tombstoneMessage(
  data: MessagePages | undefined,
  messageId: string,
  deletedAt: string,
): MessagePages | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((m) =>
        m.id === messageId ? { ...m, deletedAt, body: '', attachments: [] } : m,
      ),
    })),
  };
}

/**
 * Resolves `mine` for the viewer from the author id.
 *
 * The server forces `mine` false on a broadcast, because one payload goes to
 * every member. The sender's own tab keeps the true value from its POST
 * response, but their *other* tabs and devices only ever see the broadcast — so
 * their own message arrived there drawn as somebody else's. The author id is
 * on the payload; comparing it is all `mine` ever meant.
 */
export function withViewerMine(
  message: ChatMessageView,
  viewerId: string | null | undefined,
): ChatMessageView {
  const mine = message.mine || (Boolean(viewerId) && message.author?.id === viewerId);
  return mine === message.mine ? message : { ...message, mine };
}

/**
 * Hands out the idempotency key for a composer's sends.
 *
 * Minting a fresh key on every click meant a manual retry of a send that had
 * actually landed — the response lost to a dropped connection — posted the
 * message twice, which is exactly what the key exists to prevent. The same
 * draft now keeps its key until a send of it succeeds; a different draft, or
 * the same words sent again after a success, gets a new one.
 */
export function createDraftKeyMinter(mint: () => string) {
  let current: { fingerprint: string; key: string } | null = null;
  return {
    /** The key for this draft — reused while it is the one being retried. */
    keyFor(fingerprint: string): string {
      if (current?.fingerprint !== fingerprint) current = { fingerprint, key: mint() };
      return current.key;
    },
    /** The draft was delivered; the next send is a new message. */
    settle(): void {
      current = null;
    },
  };
}
